import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Phone, MapPin, ArrowLeft, Shield, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', phone: '', address: '', role: 'customer' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (!token || !userStr) {
      navigate('/admin/login');
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        navigate('/admin/login');
        return;
      }
    } catch (error) {
      navigate('/admin/login');
      return;
    }
    
    loadUsers();
  }, [navigate]);

  const loadUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Gagal memuat data user');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMember = async (user) => {
    try {
      const newStatus = !user.is_member;
      await api.put(`/admin/users/${user.id}/member`, null, {
        params: { is_member: newStatus }
      });
      toast.success(`User berhasil ${newStatus ? 'dijadikan member' : 'dihapus dari member'}`);
      loadUsers();
    } catch (error) {
      toast.error('Gagal mengubah status member');
    }
  };

  const handleChangeRole = async (userId, role) => {
    setUpdatingRoleId(userId);
    try {
      await api.put(`/admin/users/${userId}/role`, null, { params: { role } });
      toast.success('Role berhasil diupdate');
      loadUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal update role');
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.post('/admin/users', newUser);
      toast.success('User berhasil ditambahkan');
      setIsAddModalOpen(false);
      setNewUser({ name: '', email: '', password: '', phone: '', address: '', role: 'customer' });
      loadUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal menambahkan user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/admin/dashboard" className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Admin Panel</span>
            </Link>
            <Link to="/admin/dashboard" className="text-zinc-400 hover:text-primary flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Dashboard</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl font-heading font-bold italic uppercase text-white">Kelola User</h1>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-2 bg-primary text-black px-4 py-2 rounded-md font-bold uppercase hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add User
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-zinc-500 text-lg">Belum ada user terdaftar</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {users.map((user) => (
                <div
                  key={user.id}
                  data-testid={`user-${user.id}`}
                  className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{user.name}</h3>
                      <span className="text-xs text-zinc-400">ID: {user.id.slice(0, 8)}</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <Shield className="w-4 h-4 text-primary mt-1" />
                      <div className="w-full">
                        <div className="text-xs text-zinc-400">Role</div>
                        <div className="flex items-center gap-2 mt-1">
                          <select
                            value={user.role || 'customer'}
                            onChange={(e) => handleChangeRole(user.id, e.target.value)}
                            disabled={updatingRoleId === user.id}
                            className="flex-1 bg-black border border-zinc-800 rounded-md px-3 py-2 text-white focus:outline-none focus:border-primary"
                          >
                            <option value="customer">Customer</option>
                            <option value="kasir">Kasir</option>
                            <option value="admin">Admin</option>
                          </select>
                          {updatingRoleId === user.id && (
                            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 mt-2">
                          Kasir: bisa ubah jadwal & approve pembayaran. Admin: full akses.
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 text-primary mt-1" />
                      <div>
                        <div className="text-xs text-zinc-400">Email</div>
                        <div className="text-white text-sm">{user.email}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Phone className="w-4 h-4 text-primary mt-1" />
                      <div>
                        <div className="text-xs text-zinc-400">No. HP</div>
                        <div className="text-white text-sm">{user.phone}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-primary mt-1" />
                      <div>
                        <div className="text-xs text-zinc-400">Alamat</div>
                        <div className="text-white text-sm">{user.address}</div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-zinc-800">
                      <button
                        onClick={() => handleToggleMember(user)}
                        disabled={user.role !== 'customer'}
                        className={`w-full py-2 px-4 rounded-md font-bold text-sm uppercase transition-colors ${
                          user.role !== 'customer'
                            ? 'bg-zinc-800/40 text-zinc-500 cursor-not-allowed'
                            : user.is_member 
                            ? 'bg-red-900/20 text-red-500 hover:bg-red-900/40' 
                            : 'bg-primary/10 text-primary hover:bg-primary/20'
                        }`}
                      >
                        {user.role !== 'customer' ? 'Member N/A' : (user.is_member ? 'Remove Member' : 'Add Member')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md w-full"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-bold italic uppercase text-white">Add New User</h2>
              <button onClick={() => setIsAddModalOpen(false)} className="text-zinc-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Nama Lengkap</label>
                <input
                  type="text"
                  required
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Email</label>
                <input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Password</label>
                <input
                  type="password"
                  required
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">No. HP</label>
                <input
                  type="tel"
                  required
                  value={newUser.phone}
                  onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                  className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                  placeholder="081234567890"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Alamat</label>
                <textarea
                  required
                  value={newUser.address}
                  onChange={(e) => setNewUser({...newUser, address: e.target.value})}
                  className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary min-h-[80px]"
                  placeholder="Alamat lengkap..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                >
                  <option value="customer">Customer</option>
                  <option value="kasir">Kasir</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-black font-bold uppercase py-3 rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Plus className="w-5 h-5" />
                  )}
                  {isSubmitting ? 'Menambahkan...' : 'Add User'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
